import { ArrowRight, Baby, Banknote, CalendarCheck, ClipboardCheck, Download, FileText, Upload } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LoadingScreen } from "./auth";
import { EmptyState, Metric, WorkspaceHeader } from "./common";
import {
  api,
  assignmentStatusLabel,
  formatMoney,
  isPapiAssignment,
  isPapiService,
  orderStatusLabel,
  paymentStatusLabel,
  type AssessmentAssignment,
  type ChildProfile,
  type ClientData,
  type Order,
  type PublicData,
  type Report,
} from "./core";

export function ClientPortal() {
  const [data, setData] = useState<ClientData | null>(null);

  const load = async () => {
    const [publicData, children, orders, assignments, reports] = await Promise.all([
      api<PublicData>("/api/public"),
      api<{ children: ChildProfile[] }>("/api/children").then(result => result.children),
      api<{ orders: Order[] }>("/api/orders").then(result => result.orders),
      api<{ assignments: AssessmentAssignment[] }>("/api/assessments").then(result => result.assignments),
      api<{ reports: Report[] }>("/api/reports").then(result => result.reports),
    ]);
    setData({ publicData, children, orders, assignments, reports });
  };

  useEffect(() => {
    load().catch(error => toast.error(error.message));
  }, []);

  if (!data) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-secondary/30">
      <WorkspaceHeader title="Portal Klien" description="Pesanan, pembayaran, asesmen, dan laporan MotherSense." />
      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-8 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-4">
          <Metric icon={CalendarCheck} label="Pesanan" value={data.orders.length} />
          <Metric icon={ClipboardCheck} label="Asesmen aktif" value={data.assignments.filter(item => item.status === "assigned").length} />
          <Metric icon={FileText} label="Laporan" value={data.reports.length} />
          <Metric icon={Baby} label="Profil anak" value={data.children.length} />
        </div>

        <div className="grid gap-6 xl:grid-cols-[0.9fr_1.1fr]">
          <OrderPanel data={data} onReload={load} />
          <ClientStatusPanel data={data} onReload={load} />
        </div>

        <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
          <ClientAssessmentsSummary assignments={data.assignments} />
          <ReportsPanel reports={data.reports} />
        </div>

        <div className="grid gap-6 xl:grid-cols-[1fr_1fr]">
          <ChildrenPanel children={data.children} onReload={load} />
        </div>
      </main>
    </div>
  );
}

function OrderPanel({ data, onReload }: { data: ClientData; onReload: () => Promise<void> }) {
  const [submitting, setSubmitting] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const selectedService = data.publicData.services.find(service => service.id === selectedServiceId);
  const selectedIsPapi = isPapiService(selectedService);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const serviceId = String(form.get("serviceId") ?? "");
    const payload = {
      serviceId,
      childId: selectedIsPapi ? undefined : String(form.get("childId") ?? "") || undefined,
      participantName: String(form.get("participantName") ?? ""),
      notes: String(form.get("notes") ?? ""),
    };

    setSubmitting(true);
    try {
      await api("/api/orders", { method: "POST", body: JSON.stringify(payload) });
      toast.success("Pesanan dibuat");
      formElement.reset();
      setSelectedServiceId("");
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal membuat pesanan");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Pesan layanan</CardTitle>
        <CardDescription>Pilih layanan, lalu lakukan transfer sesuai rekening yang muncul.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="serviceId">Layanan</Label>
            <select
              id="serviceId"
              name="serviceId"
              required
              className="field-select"
              value={selectedServiceId}
              onChange={event => setSelectedServiceId(event.target.value)}
            >
              <option value="">Pilih layanan</option>
              {data.publicData.services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.title} - {formatMoney.format(service.price)}
                </option>
              ))}
            </select>
            {selectedIsPapi && (
              <p className="text-xs text-muted-foreground">
                PAPI Kostick adalah asesmen dewasa. Profil anak tidak digunakan untuk layanan ini.
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="participantName">Nama peserta</Label>
            <Input id="participantName" name="participantName" required />
          </div>
          {!selectedIsPapi && (
            <div className="grid gap-2">
              <Label htmlFor="childId">Profil anak</Label>
              <select id="childId" name="childId" className="field-select">
                <option value="">Tidak terkait anak</option>
                {data.children.map(child => (
                  <option key={child.id} value={child.id}>
                    {child.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="grid gap-2">
            <Label htmlFor="notes">Catatan</Label>
            <Textarea id="notes" name="notes" className="min-h-24" />
          </div>
          <Button disabled={submitting} type="submit">
            <CalendarCheck />
            Buat pesanan
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ClientStatusPanel({ data, onReload }: { data: ClientData; onReload: () => Promise<void> }) {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Status pesanan</CardTitle>
        <CardDescription>
          Rekening: {data.publicData.bank.bank} {data.publicData.bank.number} a.n. {data.publicData.bank.name}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {data.orders.length === 0 && <EmptyState icon={Banknote} title="Belum ada pesanan" />}
        {data.orders.map(order => (
          <div key={order.id} className="rounded-lg border p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="font-semibold text-primary">{order.service?.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {order.participantName} - {formatMoney.format(order.total)}
                </p>
              </div>
              <Badge tone={order.status === "completed" || order.status === "confirmed" ? "green" : "amber"}>
                {orderStatusLabel[order.status]}
              </Badge>
            </div>
            {order.payment && (
              <p className="mt-3 text-xs text-muted-foreground">
                Pembayaran: {paymentStatusLabel[order.payment.status]}
                {order.payment.adminNote ? ` - ${order.payment.adminNote}` : ""}
              </p>
            )}
            {(order.status === "waiting_payment" || order.payment?.status === "rejected") && (
              <PaymentUpload order={order} onReload={onReload} />
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function PaymentUpload({ order, onReload }: { order: Order; onReload: () => Promise<void> }) {
  const [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setLoading(true);
    try {
      await api(`/api/orders/${order.id}/payments`, { method: "POST", body: form });
      toast.success("Bukti transfer terkirim");
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal unggah bukti");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 flex flex-col gap-3 rounded-md bg-secondary/60 p-3 sm:flex-row sm:items-center">
      <Input type="file" name="proof" accept="image/*,application/pdf" required />
      <Button type="submit" disabled={loading} size="sm">
        <Upload />
        Unggah bukti
      </Button>
    </form>
  );
}

function ChildrenPanel({ children, onReload }: { children: ChildProfile[]; onReload: () => Promise<void> }) {
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const payload = {
      name: String(form.get("name") ?? ""),
      birthDate: String(form.get("birthDate") ?? ""),
      schoolLevel: String(form.get("schoolLevel") ?? ""),
      notes: String(form.get("notes") ?? ""),
    };
    try {
      await api("/api/children", { method: "POST", body: JSON.stringify(payload) });
      toast.success("Profil anak disimpan");
      formElement.reset();
      await onReload();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Gagal menyimpan profil anak");
    }
  };

  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Profil anak</CardTitle>
        <CardDescription>Dipakai untuk tes minat bakat dan kesiapan sekolah.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-5">
        <div className="grid gap-3">
          {children.map(child => (
            <div key={child.id} className="rounded-lg border p-4">
              <h3 className="font-semibold text-primary">{child.name}</h3>
              <p className="text-sm text-muted-foreground">
                {child.birthDate || "Tanggal lahir belum diisi"} {child.schoolLevel ? `- ${child.schoolLevel}` : ""}
              </p>
            </div>
          ))}
        </div>
        <form onSubmit={submit} className="grid gap-3 rounded-lg bg-secondary/60 p-4">
          <div className="grid gap-2">
            <Label htmlFor="child-name">Nama anak</Label>
            <Input id="child-name" name="name" required />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="birthDate">Tanggal lahir</Label>
              <Input id="birthDate" name="birthDate" type="date" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="schoolLevel">Jenjang</Label>
              <Input id="schoolLevel" name="schoolLevel" placeholder="Pra-TK, TK, SD" />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="child-notes">Catatan</Label>
            <Textarea id="child-notes" name="notes" className="min-h-20" />
          </div>
          <Button type="submit" variant="outline">
            Simpan profil
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function ReportsPanel({ reports }: { reports: Report[] }) {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Laporan</CardTitle>
        <CardDescription>Laporan PDF muncul setelah dirilis admin.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {reports.length === 0 && <EmptyState icon={FileText} title="Belum ada laporan rilis" />}
        {reports.map(report => (
          <div key={report.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-semibold text-primary">{report.title}</h3>
              <p className="text-sm text-muted-foreground">{report.fileName}</p>
            </div>
            <Button asChild size="sm">
              <a href={`/api/reports/${report.id}/download`}>
                <Download />
                Unduh
              </a>
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function ClientAssessmentsSummary({ assignments }: { assignments: AssessmentAssignment[] }) {
  return (
    <Card className="rounded-lg">
      <CardHeader>
        <CardTitle className="font-display text-2xl text-primary">Asesmen</CardTitle>
        <CardDescription>Isi form yang sudah di-assign admin.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {assignments.length === 0 && <EmptyState icon={ClipboardCheck} title="Belum ada asesmen" />}
        {assignments.map(item => (
          <div key={item.id} className="flex flex-col gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-primary">{item.template?.title}</h3>
                {isPapiAssignment(item) && <Badge>PAPI</Badge>}
              </div>
              <p className="text-sm text-muted-foreground">{item.template?.description}</p>
            </div>
            {item.status === "assigned" ? (
              <Button asChild size="sm">
                <Link to={`/portal/asesmen/${item.id}`}>
                  {isPapiAssignment(item) ? "Lanjutkan" : "Isi"}
                  <ArrowRight />
                </Link>
              </Button>
            ) : (
              <Badge tone={item.status === "reviewed" ? "green" : "amber"}>{assignmentStatusLabel[item.status]}</Badge>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
